import createSelector from 'lib/web/createSelector'
import { EmptyArray, sortBy } from 'lib/array/arrayUtils'

export const getUserDestinationAlerts = createSelector(
  (state: App.State) => state.destination.destinations,
  (state: App.State) => state.auth.destinationAlerts,
  (destinations, userAlerts) => userAlerts.map(placeId => destinations[placeId]).filter(Boolean),
)

export type PopularRegionView = App.PopularRegion & { isAdded: boolean };
export const getPopularRegionList = createSelector(
  (state: App.State) => state.destination.popularRegions,
  (state: App.State) => state.auth.popularRegionAlerts,
  (popularRegions, userRegions) => sortBy(Object.values(popularRegions),
    region => region.tag, 'asc').map((region) => ({
    ...region,
    isAdded: userRegions.includes(region.tag),
  })),
)

export const getUserAllAlertsList = createSelector(
  (state: App.State) => getPopularRegionList(state),
  (state: App.State) => getUserDestinationAlerts(state),
  (popularRegions, userAlerts) => {
    const popularRegionList = popularRegions.filter(popularRegion => popularRegion.isAdded)
    return [
      ...userAlerts,
      ...popularRegionList.map(region => ({
        placeId: '',
        availableOffers: {
          administrativeAreaLevel1: [],
          continent: [],
          country: [],
          local: [],
          popular: region.availableOffers,
        },
        continent: '',
        country: '',
        administrativeAreaLevel1: '',
        local: '',
        locationAlert: {
          value: region.tag,
          level: 'popular',
        },
        geocode: {
          lng: null,
          lat: null,
        },
      })),
    ]
  },
)

export type FeaturedDestinationView = App.Destination & { isAdded: boolean };
export const getFeaturedDestinationList = createSelector(
  (state: App.State) => state.destination.featuredDestinations,
  (state: App.State) => state.destination.destinations,
  (state: App.State) => state.auth.destinationAlerts,
  (featuredDestinations, destinations, userDestinations) => featuredDestinations.map(placeId => ({
    ...destinations[placeId],
    isAdded: userDestinations.includes(placeId),
  })),
)
export const getIsFetchingFeaturedDestinations = (state: App.State) => state.destination.fetchingFeaturedDestinations

export const getHotelsPopularDestinations = createSelector(
  (state: App.State) => state.destination.featuredDestinations,
  (state: App.State) => state.destination.destinations,
  (
    featuredDestinations,
    destinations,
  ): App.SearchDestination[] => featuredDestinations.map(placeId => {
    const destination = destinations[placeId]

    return {
      searchType: 'destination',
      value: destination.placeId,
      format: { mainText: destination.locationAlert.value },
    }
  }),
)
export const getIsFetchingTrendingDestinations = (state: App.State) => state.destination.fetchingTrendingDestinations

export const getHotelsTrendingDestinations = createSelector(
  (state: App.State) => state.destination.trendingDestinations,
  (trendingDestinations): App.SearchDestination[] => trendingDestinations.map(destination => {
    return {
      searchType: 'destination',
      value: destination.placeId,
      format: { mainText: destination.primaryText },
    }
  }),
)

export const getToursPopularDestinations = (state: App.State) => state.destination.tours?.popularDestinations ?? EmptyArray
export const getIsFetchingToursPopularDestinations = (state: App.State) => state.destination.tours?.isFetchingPopularDestinations
